import React, { useEffect, useRef, useCallback } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import theme from '@src/styles/theme';
import AILoadingOverlay from '@src/new-components/AILoadingOverlay';
import useTranscriptPolling from '@src/hooks/useTranscriptPolling';
import {
  EXTRACTION_STATUS,
  FETCH_EXTRACTION_STATUS_INTERVAL,
} from '@src/constants/project';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { getSourcePreparationProgress } from '@src/apis/project';
import {
  convertExtractionProgressToBlocks,
  isBlockScopedLoading,
} from '@src/services/project';
import actions from '@src/redux/actions';
import route from '@src/constants/route';
import { StyledProjectDetail } from '../index.style';
import Content from './Content';
import ProjectOnMobile from '../../Project/ProjectOnMobile';
import Header from '../component/ProjectDetailHeader';

const DubbingDetail = ({
  extractionStatus,
  onExtractionComplete,
  projectId,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const intervalRef = useRef(null);
  const { getFeatureValue } = useFeatureFlags();
  const { user } = useSelector((state) => state.auth);
  const source = useSelector((state) => state.project.source);

  const isExtracting = extractionStatus === EXTRACTION_STATUS.PENDING;

  const transcribeChunkEnabled = getFeatureValue(
    FEATURE_KEYS.TRANSCRIBE_CHUNK,
    {
      userId: user?.id,
    },
  );

  // In the new video-dubbing editor the extraction loading lives inside the
  // block list (Content renders it there), so the full-screen overlay below
  // must stand down — otherwise both would show and the blur would cover the
  // video preview and the timeline the design keeps visible.
  const timelineTracksEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_TIMELINE_TRACKS,
    { userId: user?.id, email: user?.email, phoneNumber: user?.phoneNumber },
  );
  const blockScopedLoading = isBlockScopedLoading({
    source,
    timelineTracksEnabled,
  });

  // New flow (transcribe-chunk ON): poll each batch and render blocks incrementally.
  const pollChunkProgress = useCallback(async () => {
    try {
      const progress = await getSourcePreparationProgress(projectId);
      const { status, extractionProgress } = progress?.result || {};

      if (extractionProgress?.batches) {
        const { blocks, blockIds } =
          convertExtractionProgressToBlocks(extractionProgress);
        if (blockIds.length > 0) {
          dispatch(actions.project.setStreamingBlocks(blocks, blockIds));
        }
      }

      if (status === EXTRACTION_STATUS.SUCCESS) {
        onExtractionComplete();
      } else if (status === EXTRACTION_STATUS.FAILURE) {
        dispatch(actions.project.setIsLoadingProjectDetail(false));
        dispatch(
          actions.noti.push({
            severity: 'error',
            message: 'createProjectFailed',
          }),
        );
        history.replace(route.PROJECTS);
      }
    } catch {
      dispatch(actions.project.setIsLoadingProjectDetail(false));
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'createProjectFailed',
        }),
      );
      history.replace(route.PROJECTS);
    }
  }, [projectId, onExtractionComplete, dispatch, history]);

  useEffect(() => {
    if (!isExtracting || !transcribeChunkEnabled) return undefined;

    intervalRef.current = setInterval(
      pollChunkProgress,
      FETCH_EXTRACTION_STATUS_INTERVAL,
    );
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isExtracting, transcribeChunkEnabled, pollChunkProgress]);

  // Current flow (transcribe-chunk OFF): only active when flag is off to avoid double-polling.
  useTranscriptPolling({
    projectId,
    isActive: isExtracting && Boolean(projectId) && !transcribeChunkEnabled,
    onComplete: onExtractionComplete,
  });

  if (isMobile) {
    return (
      <div style={{ padding: '0 16px' }}>
        <ProjectOnMobile />
      </div>
    );
  }

  return (
    <StyledProjectDetail>
      <div className="container-main-panel" style={{ position: 'relative' }}>
        <Header />
        <Content
          isExtracting={isExtracting}
          extractProgressEnabled={transcribeChunkEnabled}
        />
        {/* Current flow: full-screen blur overlay while extracting */}
        {isExtracting && !transcribeChunkEnabled && !blockScopedLoading && (
          <AILoadingOverlay text={t('aiProcessing')} blurAmount="8px" />
        )}
      </div>
    </StyledProjectDetail>
  );
};

export default DubbingDetail;
